import { idmsApi } from "./api";
import {
  API_FORGOT_PASSWORD_ENDPOINT,
  API_RESET_PASSWORD_ENDPOINT,
  API_VALIDATE_RESET_TOKEN_ENDPOINT,
} from "../../constants/endpoints";

/** Payload for requesting a password reset link. */
export interface ForgotPasswordRequest {
  email: string;
}

/** Generic, non-enumerating response returned by the forgot-password endpoint. */
export interface ForgotPasswordResponse {
  message: string;
}

/** Payload for completing a password reset with the emailed token. */
export interface ResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
  confirmPassword: string;
}

/** Payload for checking, on page load, whether an emailed reset link is still valid. */
export interface ValidateResetTokenRequest {
  email: string;
  token: string;
}

/** Result of the non-consuming reset-token check. */
export interface ValidateResetTokenResponse {
  isValid: boolean;
}

const accountApi = idmsApi.injectEndpoints({
  endpoints: (builder) => ({
    // Requests a password reset link. Response is intentionally identical whether or not the account exists.
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (request) => ({
        url: API_FORGOT_PASSWORD_ENDPOINT,
        method: "POST",
        body: request,
      }),
    }),

    // Completes the reset using the emailed token. Returns 204 No Content on success.
    resetPassword: builder.mutation<void, ResetPasswordRequest>({
      query: (request) => ({
        url: API_RESET_PASSWORD_ENDPOINT,
        method: "POST",
        body: request,
      }),
    }),

    // Non-consuming check of the emailed token, run on page load so an expired/invalid link is
    // shown before the reset form is presented. The submit-time check in resetPassword is retained.
    validateResetToken: builder.query<ValidateResetTokenResponse, ValidateResetTokenRequest>({
      query: (request) => ({
        url: API_VALIDATE_RESET_TOKEN_ENDPOINT,
        method: "POST",
        body: request,
      }),
    }),
  }),
});

export const {
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useValidateResetTokenQuery,
} = accountApi;
